import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { fadeUp } from "../lib/motion";
import Footer from "../components/sections/Footer";

export default function LegalPage({ overline, title, lastUpdated, sections }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="legal-page" className="min-h-screen bg-[color:var(--bg-base)]">
      {/* Lightweight nav with logo only */}
      <nav className="border-b border-[color:var(--border-light)] bg-[color:var(--bg-base)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-20 flex items-center">
          <Link to="/" data-testid="legal-nav-logo" className="flex items-baseline">
            <span
              className="font-serif-display text-xl font-semibold text-[color:var(--charcoal)]"
              style={{ letterSpacing: "0.08em" }}
            >
              THE FOUNDATION EDIT
            </span>
          </Link>
        </div>
      </nav>

      <main className="py-24 sm:py-32">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <motion.header {...fadeUp} className="mb-16 sm:mb-20">
            <p className="overline mb-6">— {overline}</p>
            <h1
              data-testid="legal-page-title"
              className="font-serif-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[color:var(--charcoal)] leading-[1.05]"
            >
              {title}
            </h1>
            <div className="mt-8 w-16 h-px bg-[color:var(--gold)]" />
            <p className="mt-6 text-sm tracking-[0.18em] uppercase text-[color:var(--text-secondary)]" data-testid="legal-last-updated">
              Last updated: {lastUpdated}
            </p>
          </motion.header>

          <motion.div {...fadeUp} className="space-y-14">
            {sections.map((s, i) => (
              <section
                key={s.heading}
                data-testid={`legal-section-${i + 1}`}
                className="border-t border-[color:var(--border-light)] pt-10"
              >
                <div className="flex items-baseline gap-5 mb-5">
                  <span className="font-serif-display italic text-[color:var(--gold)] text-lg sm:text-xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h2 className="font-serif-display text-2xl sm:text-3xl font-medium text-[color:var(--charcoal)] leading-tight">
                    {s.heading}
                  </h2>
                </div>
                {s.body.map((para, idx) => (
                  <p
                    key={idx}
                    className="text-[15px] sm:text-base text-[color:var(--text-secondary)] leading-[1.85] mb-4 last:mb-0 pl-8 sm:pl-10"
                  >
                    {para}
                  </p>
                ))}
                {s.contact && (
                  <p className="text-[15px] sm:text-base text-[color:var(--charcoal)] leading-relaxed pl-8 sm:pl-10">
                    Email:{" "}
                    <a
                      href={`mailto:${s.contact}`}
                      className="underline decoration-[color:var(--gold)] underline-offset-4 hover:text-[color:var(--gold)] transition-colors"
                    >
                      {s.contact}
                    </a>
                  </p>
                )}
              </section>
            ))}
          </motion.div>

          <motion.div {...fadeUp} className="mt-24 pt-10 border-t border-[color:var(--border-light)] text-center">
            <Link
              to="/"
              data-testid="legal-back-link"
              className="overline hover:text-[color:var(--charcoal)] transition-colors"
            >
              — Return to The Foundation Edit
            </Link>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
